import { User } from 'src/app/component/auth/model/user.model';
import { TrackingHistoryService } from './../../../../common-components/tracking-history/service/tracking-history.service';
import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormGroupDirective, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { CoePeople } from 'src/app/model/coes/coe-people.model';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { StudentService } from '../../../service/student.service';
import { UserProfileService } from 'src/app/component/user-profile/service/user-profile.service';

@Component({
    selector: 'edit-child-demographic-parents',
    templateUrl: './edit-child-demographic-parents.component.html',
    styleUrls: ['./edit-child-demographic-parents.component.scss']
})
export class EditChildDemographicParentsComponent implements OnInit {
    @Input() studentId!: number;
    @Input() editing: boolean;
    @Output() editingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('childParentForm') childParentForm: FormGroupDirective;
    editingForm!: UntypedFormGroup;
    editingId: number | undefined;

    parentsBackup: CoePeople[] = [];
    parents: CoePeople[] = [];
    parentsToSubmit: CoePeople[] = [];
    parentIds: string = '';
    user: User;

    isLoading: boolean = false;
    isSaving: boolean = false;

    promises: Array<any> = [];

    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    // Table display
    displayedColumns: string[] = ['EDIT', 'COE_PEOPLE_TYPE.TYPE', 'LAST_NAME', 'FIRST_NAME', 'PHONE_NUMBER', 'EMAIL_ADDRESS'];

    // Table results
    tableDataSources: MatTableDataSource<CoePeople>[] = [];

    // Changes
    formChanges: any = {};

    getPropertyPipe = new GetPropertyPipe();

    subs: Subscription[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private studentService: StudentService,
        private titlecasePipe: TitleCasePipe,
        private userProfileService: UserProfileService,
        private trackingHistoryService: TrackingHistoryService
    ) {
        this.editingForm = fb.group({
            lastName: [''],
            firstName: [''],
            preferredPhoneNumber: [''],
            preferredEmail: ['', [Validators.email]]
        });
    }

    ngOnInit(): void {}

    init() {
        this.isLoading = true;
        this.parentsToSubmit = [];
        this.cancelEditParent();

        this.getParents();
        this.getUserProfile();
        Promise.all(this.promises).then((res) => {
            this.updateDataSource();

            this.initCurrentChanges();
            this.setCurrentChanges();

            this.isLoading = false;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.editing) {
            this.displayedColumns = ['EDIT', 'COE_PEOPLE_TYPE.TYPE', 'LAST_NAME', 'FIRST_NAME', 'PHONE_NUMBER', 'EMAIL_ADDRESS'];
            this.editingForm.enable();
        } else {
            this.displayedColumns = ['COE_PEOPLE_TYPE.TYPE', 'LAST_NAME', 'FIRST_NAME', 'PHONE_NUMBER', 'EMAIL_ADDRESS'];
            this.editingForm.disable();
            if (changes['editing']) {
                this.init();
            }
        }
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    subscribers() {
        let formSub = this.editingForm.valueChanges.subscribe(() => {
            let changes = this.editingForm.getRawValue();
            Object.keys(changes).forEach((key) => {
                this.formChanges[key].CHANGED_VALUE = changes[key];
            });

            this.updateDataSource();
        });
        this.subs.push(formSub);
    }

    getParents() {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.getParents(this.studentId).subscribe((res) => {
                if (res.parents) {
                    this.parents = res.parents;
                    this.parentIds = this.parents.map((p) => p.ID).join(',');

                    this.parents.forEach((parent) => {
                        switch (parent.COE_PEOPLE_TYPE.ID) {
                            case 1: //Current Male Parent/Guardian
                                parent.COE_PEOPLE_TYPE.TYPE = 'Current Parent/Guardian 1';
                                break;
                            case 2: //Current Female Parent/Guardian
                                parent.COE_PEOPLE_TYPE.TYPE = 'Current Parent/Guardian 2';
                                break;
                            case 3: //Legal Father
                                parent.COE_PEOPLE_TYPE.TYPE = 'Legal Parent/Guardian 1';
                                break;
                            case 4: //Legal Mother
                                parent.COE_PEOPLE_TYPE.TYPE = 'Legal Parent/Guardian 2';
                                break;
                        }
                    });

                    this.parentsBackup = JSON.parse(JSON.stringify(this.parents));

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getUserProfile() {
        let deferred = new Promise((resolve, reject) => {
            this.userProfileService.getMyProfile().subscribe((res) => {
                if (res?.user) {
                    this.user = res.user;

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });
        this.promises.push(deferred);
    }

    submit() {
        this.generateParentsToSubmit();

        if (this.editingId) {
            return new Promise((resolve, reject) => {
                reject({ MSG: 'Please finish editing Parent/Guardians.', SUCCESS: false });
            });
        } else if (this.parentsToSubmit.length > 0) {
            this.isSaving = true;
            this.promises = [];

            this.saveParents();
            this.createTrackingHistory();

            return new Promise((resolve, reject) => {
                Promise.all(this.promises).then(
                    (res) => {
                        this.ngOnInit();
                        this.isSaving = false;
                        resolve({ MSG: 'Child Parents succcessfuly updated.', SUCCESS: true });
                    },
                    (err) => {
                        this.isSaving = false;
                        reject({ MSG: 'Child Parents has errors.', SUCCESS: false });
                    }
                );
            });
        } else {
            return new Promise((resolve, reject) => {
                resolve({});
            });
        }
    }

    saveParents() {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.updateParents(this.parentsToSubmit).subscribe((res) => {
                if (res?.msg) {
                    if (res.msg.SUCCESS) {
                        resolve(res);
                    } else {
                        reject(res);
                    }
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    createTrackingHistory() {
        let deferred = new Promise((resolve, reject) => {
            let payload = this.getTrackingHistories();

            if (payload.editedFields.length > 0) {
                this.trackingHistoryService.create(payload).subscribe((res) => {
                    resolve(res);
                });
            } else {
                resolve({});
            }
        });

        this.promises.push(deferred);
    }

    updateDataSource() {
        this.tableDataSources = [];

        setTimeout(() => {
            // Data
            let dataSource = new MatTableDataSource<CoePeople>();
            dataSource.data = this.parents;

            // Paginator
            //dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            dataSource.sort = this.sorts.toArray()[0];
            const defaultSort: Sort = { active: 'COE_PEOPLE_TYPE.TYPE', direction: 'asc' };
            dataSource.sort.active = defaultSort.active;
            dataSource.sort.direction = defaultSort.direction;
            dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);
        });
    }

    // Edit Function
    editParent(parent: CoePeople) {
        this.setEditing(parent);
    }
    confirmEditParent() {
        this.update(this.editingForm);
        this.cancelEdit();
    }
    cancelEditParent() {
        this.cancelEdit();
    }

    // Reset Function
    private cancelEdit() {
        this.resetEditing();
    }

    private setEditing(parent: CoePeople) {
        this.editingId = parent.ID;
        this.editingForm.patchValue({
            lastName: parent.LAST_NAME,
            firstName: parent.FIRST_NAME,
            preferredPhoneNumber: parent.PHONE_NUMBER,
            preferredEmail: parent.EMAIL_ADDRESS
        });
        this.editingForm.markAsPristine();
    }

    private resetEditing() {
        this.editingForm.patchValue({ lastName: '', firstName: '', preferredPhoneNumber: '', preferredEmail: '' });
        this.editingId = undefined;
    }

    private update(form: UntypedFormGroup) {
        let parentIndex = this.parents.findIndex((parent) => parent.ID === this.editingId);

        this.parents[parentIndex].LAST_NAME = form.get('lastName')?.value;
        this.parents[parentIndex].FIRST_NAME = form.get('firstName')?.value;
        this.parents[parentIndex].PHONE_NUMBER = form.get('preferredPhoneNumber')?.value;
        this.parents[parentIndex].EMAIL_ADDRESS = form.get('preferredEmail')?.value;

        this.updateChanges(this.parents[parentIndex]);
    }

    generateParentsToSubmit() {
        this.parentsToSubmit = [];

        this.parents.forEach((p1: CoePeople, index) => {
            if (p1.LAST_NAME !== this.parentsBackup[index].LAST_NAME) {
                this.parentsToSubmit.push(p1);
            } else if (p1.FIRST_NAME !== this.parentsBackup[index].FIRST_NAME) {
                this.parentsToSubmit.push(p1);
            } else if (p1.PHONE_NUMBER !== this.parentsBackup[index].PHONE_NUMBER) {
                this.parentsToSubmit.push(p1);
            } else if (p1.EMAIL_ADDRESS !== this.parentsBackup[index].EMAIL_ADDRESS) {
                this.parentsToSubmit.push(p1);
            }
        });
    }

    initCurrentChanges() {
        // Parents
        this.parents.forEach((parent) => {
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Last Name'] = {
                CHANGED_FIELD: parent.COE_PEOPLE_TYPE.TYPE + ' Last Name',
                VALUE: '',
                CHANGED_VALUE: '',
                ID: parent.ID
            };
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' First Name'] = {
                CHANGED_FIELD: parent.COE_PEOPLE_TYPE.TYPE + ' First Name',
                VALUE: '',
                CHANGED_VALUE: '',
                ID: parent.ID
            };
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Phone Number'] = {
                CHANGED_FIELD: parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Phone Number',
                VALUE: '',
                CHANGED_VALUE: '',
                ID: parent.ID
            };
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Email'] = {
                CHANGED_FIELD: parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Email',
                VALUE: '',
                CHANGED_VALUE: '',
                ID: parent.ID
            };
        });
    }

    setCurrentChanges() {
        // Parents
        this.parents.forEach((parent) => {
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Last Name'].VALUE = parent.LAST_NAME === null ? '' : parent.LAST_NAME;
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' First Name'].VALUE = parent.FIRST_NAME === null ? '' : parent.FIRST_NAME;
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Phone Number'].VALUE = parent.PHONE_NUMBER === null ? '' : parent.PHONE_NUMBER;
            this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Email'].VALUE = parent.EMAIL_ADDRESS === null ? '' : parent.EMAIL_ADDRESS;
        });

        // Set same value
        Object.keys(this.formChanges).forEach((key) => {
            this.formChanges[key].CHANGED_VALUE = this.formChanges[key].VALUE;
        });
    }

    updateChanges(parent: CoePeople) {
        this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Last Name'].CHANGED_VALUE = parent.LAST_NAME;
        this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' First Name'].CHANGED_VALUE = parent.FIRST_NAME;
        this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Phone Number'].CHANGED_VALUE = parent.PHONE_NUMBER ? parent.PHONE_NUMBER : '';
        this.formChanges[parent.COE_PEOPLE_TYPE.TYPE + ' Preferred Email'].CHANGED_VALUE = parent.EMAIL_ADDRESS ? parent.EMAIL_ADDRESS : '';

        Object.keys(this.formChanges).forEach((key) => {
            this.formChanges[key].CHANGED = this.formChanges[key].VALUE !== this.formChanges[key].CHANGED_VALUE;
        });
    }

    getChanges() {
        let changes: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                changes.push(this.formChanges[key]);
            }
        });

        return changes;
    }

    getTrackingHistories() {
        let trackingHistories: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                let trackingHistory = this.formChanges[key];
                trackingHistory.CHANGED_BY = this.user.USER_ID;
                trackingHistory.CHANGED_FORM = 'Parent Information';
                trackingHistory.CHANGED_FORM_ID = this.formChanges[key].ID;
                trackingHistory.CHANGED_VALUE = trackingHistory.VALUE; // VALUE holds current value, CHANGED_VALUE is used in back-end to record previous data.

                trackingHistories.push(trackingHistory);
            }
        });

        return { editedFields: trackingHistories };
    }
}
